
import React, { useState, useEffect } from 'react';
import DocumentViewer from '@/components/compliance/repository/DocumentViewer';
import ComplianceRulesViewer from '@/components/compliance/repository/ComplianceRulesViewer';
import DocumentSelector from '@/components/compliance/repository/DocumentSelector';
import RepositoryHeader from '@/components/compliance/repository/RepositoryHeader';
import { useComplianceDocumentSelector } from '@/hooks/useComplianceDocumentSelector';
import { complianceDocuments } from '@/data/complianceDocuments';
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

const DocumentViewerSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <Skeleton className="h-10 w-full max-w-md mb-4" />
    <div className="grid gap-6 md:grid-cols-5">
      <Skeleton className="h-[400px] md:col-span-3" />
      <Skeleton className="h-[400px] md:col-span-2" />
    </div>
  </div>
);

const ComplianceRepository: React.FC = () => {
  const { 
    selectedDocumentId, 
    setSelectedDocumentId, 
    selectedDocument 
  } = useComplianceDocumentSelector(complianceDocuments);
  
  const [isLoading, setIsLoading] = useState(true);

  // Optimize with faster initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 50); // Reduced from 100ms to 50ms for more responsive feel
    return () => clearTimeout(timer);
  }, []);

  if (!selectedDocument) {
    return (
      <div className="space-y-6">
        <RepositoryHeader />
        <p>No documents available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <RepositoryHeader />

      {isLoading ? (
        <DocumentViewerSkeleton />
      ) : (
        <>
          <LazyLoad delay={50}>
            <DocumentSelector
              documents={complianceDocuments}
              selectedDocumentId={selectedDocumentId}
              onDocumentChange={setSelectedDocumentId}
            />
          </LazyLoad>

          <div className="grid gap-6 md:grid-cols-5">
            <LazyLoad delay={100}>
              <DocumentViewer 
                documentId={selectedDocument.id}
                content={selectedDocument.content} 
              />
            </LazyLoad>
            
            <LazyLoad delay={150}>
              <ComplianceRulesViewer 
                rules={selectedDocument.rules} 
              />
            </LazyLoad>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceRepository;
